import react, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react"
import { NavigationFunction as RootNavigation, Pages } from "../Navigation/Routers/root"
import { useNavigate } from "react-router"
import { useDispatch } from 'react-redux'
import { setStates } from "../Store/StateHandlers/active-slice"
import { useUser } from "../Services"
import { useTranslation } from "react-i18next"
import "../Translations/index"

import { Modal, InputFieldList, InputFieldList_PropHandle, InputField, Button } from "../Components"
import { colors, frames, fonts, glyphs, css, useMediaQuery, mediaQueriesNumbers } from "../Styles/index"
import { ListData_ValueTypes, Data, DataValue } from '../Definitions/valueTypes'
import { TableStyles_Types, ButtonTypes } from "../Definitions"
import { InputField_Handles } from "../Components/BaseComponents/input"
import { useQuery } from "@apollo/client"
import { userInfo } from "os"
import { GenericDataParameters } from "../Components/Tables/_Tables/useColumnConstructor"
import { useEditableColumnConstructor } from "../Components/Tables/_Tables/useEditableColumnConstructor"
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table"
import { ReactiveTable } from "../Components/Tables/reactiveTableRenderer"

// Modals on Login Page
enum Auth_States{
  Login,
  CreateUser,
  SiteList,
  ForgotPassword,
  ResetPassword,
}

// #region | Login Modal
function Login({setNewState}:{setNewState:Dispatch<SetStateAction<Auth_States>>}) {
  const mobile = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileMax}px)`); 
  const navigator = useNavigate()
  const {t} = useTranslation()
  const inputRef=useRef<InputFieldList_PropHandle>(null)
  const [message, setMessage] = useState<string|null>(null)
  
  const { doLogin } = useUser()

  // Use "Enter" to submit
  onkeydown = (event) => {
    if (event.code === "Enter") {
      LoginAction()
    }
  }

  const LoginAction = () => {
    if (inputRef && inputRef.current) {
      if (inputRef.current.refs && inputRef.current.refs.current) {
        var Username = inputRef.current.refs.current[0].context
        var Password = inputRef.current.refs.current[1].context

        if(!Username) {
          inputRef.current.refs.current[0].setError()
          inputRef.current.refs.current[0].focus()
          setMessage("User name is missing")
        }
        else if (!Password) {
          inputRef.current.refs.current[1].setError()
          inputRef.current.refs.current[1].focus()
          setMessage("Password is missing")
        }
        else {
          doLogin(Username, Password).then (e => {
            if (e) {setNewState(Auth_States.SiteList)} else {
              setMessage("could not login")
            }
          })
        }
        setTimeout(() => {
          setMessage(null);
        }, 2000);
      }
    }
  }

  return (
    <frames.DecorBg>      
      <Modal 
        modalHeader={t("loginFlow.login.header")}
        overrides={{ 
          isPrimaryComponent: true,
          background: colors.blueDarkPrimary
        }}
        onCloseBtn={() => navigator(-1)}
      >
        <>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px"}}>
            <InputFieldList ref={inputRef}
              startEnhancers={[glyphs.User({size: "25px"}), glyphs.Password({size: "25px"})]}
              placeholder={[t("user.userName")+"...", t("user.password")+"..."]}
              inputTypes={["text", "password"]}
              isRequired={[true, true, false]}
              listStyle="vertical"
              listGap="10px"
            />

            {/* Login button */}
            <Button content={t("buttons.loginbtn")} onClick={LoginAction}
              buttonType={ButtonTypes.primaryButton}
              overrides={{
                baseButton: {...css.button_FitContent}
              }}
            />
            {/* Create user button */}
            <div style={{display: "flex", flexDirection: "row", gap: mobile? "20px" : "40px", marginTop: "10px"}}>
              <div style={{display: "flex", alignItems: "center", flexDirection: "column"}}>
                <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "center", marginBottom: "5px" }}>{t("loginFlow.login.footerText")}</p>
                <Button
                  content={t("loginFlow.login.create")} 
                  onClick={() => {setNewState(Auth_States.CreateUser)}}
                  buttonType={ButtonTypes.textButton}
                  overrides={{
                    baseButton: {...css.button_TextUnderline}
                  }}
                />
              </div>
              {/* Reset password button */}
              <div style={{display: "flex", alignItems: "center", flexDirection: "column"}}>
                <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "center", marginBottom: "5px" }}>{t("loginFlow.login.forgotPassword")}</p>
                <Button
                  content={t("loginFlow.login.resetPassword")} 
                  onClick={() => {setNewState(Auth_States.ForgotPassword)}}
                  buttonType={ButtonTypes.textButton}
                  overrides={{
                    baseButton: {...css.button_TextUnderline}
                  }}
                />
              </div>
            </div>
            {message && <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, position: "absolute", top: mobile? "250px" : "275px" }}>{message}</p>}
          </div>
        </>
      </Modal>
    </frames.DecorBg>
  )
}
// ---------------------------------------------------
//#endregion

// #region | Forgot Password Modal
function ForgotPassword({setNewState}:{setNewState:Dispatch<SetStateAction<Auth_States>>}) {
  const mobile = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileMax}px)`); 
  const {t} = useTranslation()
  const inputRef = useRef<InputField_Handles>(null); 
  const [message, setMessage] = useState<string|null>(null)
  const {createUserResetToken} = useUser();

  const ForgotPasswordAction = () => {
    const Username = inputRef.current?.context;
    if (!Username) {
      inputRef.current?.focus();
      inputRef.current?.setError();
      setMessage(t("loginFlow.forgotPassword.error"))
    } else {
      createUserResetToken(Username).then(success => {
        if (success) setNewState(Auth_States.ResetPassword)
        else setMessage("hej")
      }).catch(err => {
        setMessage("øv")
      });
    }
    setTimeout(() => {
      setMessage(null);
    }, 2000);
  };

  return (
    <frames.DecorBg>      
      <Modal 
        modalHeader={t("loginFlow.login.forgotPassword")} 
        overrides={{ 
          isPrimaryComponent: true,
          background: colors.blueDarkPrimary
        }}
        onGoBack={() => { setNewState(Auth_States.Login) }}
      >
        <>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", width: mobile? "280px" : "fit-content"}}>
            {/* If no email, contact support. */}
            <p style={{ ...fonts.paragraph, ...fonts.textShadow, ...fonts.fontSize3, color: colors.greyLightTertiary, textAlign: "center", whiteSpace: 'pre-wrap', maxWidth: "280px" }}>
              {t("loginFlow.forgotPassword.text")}
            </p>
            {/* Don't close the window. */}
            <p style={{ ...fonts.paragraph, ...fonts.textShadow, ...fonts.fontSize3, color: colors.greyLightTertiary, textAlign: "center", whiteSpace: 'pre-wrap', maxWidth: "280px" }}>
              {t("loginFlow.forgotPassword.text2")}
            </p>
            <InputField ref={inputRef}
              startEnhancer={glyphs.User({size: "25px"})}
              placeholder={t("user.userName")+"..."}
              inputType={"text"}
              isRequired={true}
            />
            {/* Reset password button */}
            <Button content={t("loginFlow.login.resetPassword")} onClick={ForgotPasswordAction}
              buttonType={ButtonTypes.primaryButton}
              overrides={{
                baseButton: {...css.button_FitContent},
              }}
            />
            {/* Contact Support */}
            <div style={{marginTop: "10px", display: "flex", alignItems: "center", flexDirection: "column"}}>
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "center", marginBottom: "5px" }}>{t("loginFlow.forgotPassword.noEmail")}</p>
              <Button
                content={t("loginFlow.forgotPassword.contactSupport")} 
                onClick={() => window.location.href = 'mailto:support@agrisys.dk'}
                buttonType={ButtonTypes.textButton}
                overrides={{
                  baseButton: {...css.button_TextUnderline}
                }}
              />
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "center", marginTop: "2px" }}>support@agrisys.dk</p>
            </div>
            {message && <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, position: "absolute", top: mobile ? "370px" : "343px" }}>{message}</p>}
          </div>
        </>
      </Modal>
    </frames.DecorBg>
  )
}
// ---------------------------------------------------
//#endregion

// #region | Forgot Password Modal
function ResetPassword({setNewState}:{setNewState:Dispatch<SetStateAction<Auth_States>>}) {
  const mobile = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileMax}px)`); 
  const {t} = useTranslation()
  const inputRef=useRef<InputFieldList_PropHandle>(null)
  const {tokenResetPassword} = useUser()
  const [message, setMessage] = useState<string|null>(null)

  const ResetPasswordAction = () => {
    if (inputRef && inputRef.current) {
      if (inputRef.current.refs && inputRef.current.refs.current) {
        const token = inputRef.current.refs.current[0].context
        const Password = inputRef.current.refs.current[1].context
        const secondPassword = inputRef.current.refs.current[2].context
        if(!token) {
          inputRef.current.refs.current[0].setError()
          inputRef.current.refs.current[0].focus()
        }
        else if (!Password || secondPassword !== Password) {
          inputRef.current.refs.current[1].setError()
          inputRef.current.refs.current[1].focus()
        }
        else {
          tokenResetPassword(token, Password).then(success => {
            if (success) setNewState(Auth_States.Login)
            else setMessage(t("loginFlow.resetPassword.error"))
          }).catch(err => {
            setMessage("øvbøv")
          })
        }
        setTimeout(() => {
          setMessage(null);
        }, 2000);
      }
    }
  }

  return (
    <frames.DecorBg>      
      <Modal 
        modalHeader={t("loginFlow.login.resetPassword")}
        overrides={{ 
          isPrimaryComponent: true,
          background: colors.blueDarkPrimary
        }}
      >
        <>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", width: mobile? "280px" : "fit-content"}}>
            {/* A code has been sent */}
            <p style={{ ...fonts.paragraph, ...fonts.textShadow, ...fonts.fontSize3, color: colors.greyLightTertiary, textAlign: "center", whiteSpace: 'pre-wrap', maxWidth: "280px" }}>
              {t("loginFlow.resetPassword.text")}            
            </p>
            <InputFieldList ref={inputRef}
              startEnhancers={[glyphs.Password({size: "25px"}), glyphs.Password({size: "25px"}), glyphs.Password({size: "25px"})]}
              placeholder={[t("loginFlow.resetPassword.temporaryCode")+"...", t("loginFlow.resetPassword.newPassword")+"...", t("loginFlow.resetPassword.repeatPassword")+"..."]}
              inputTypes={["string", "password", "password"]}
              isRequired={[true, true, true]}
              listStyle="vertical"
              listGap="10px"
            />
            {/* Create new password button */}
            <Button content={t("loginFlow.resetPassword.createNewPassword")} onClick={ResetPasswordAction}
              buttonType={ButtonTypes.primaryButton}
              overrides={{
                baseButton: {...css.button_FitContent}
              }}
            />
            {/* Contact Support */}
            <div style={{marginTop: "10px", display: "flex", alignItems: "center", flexDirection: "column"}}>
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "center", marginBottom: "5px" }}>{t("loginFlow.resetPassword.text2")}</p>
              <Button
                content={t("loginFlow.forgotPassword.contactSupport")} 
                onClick={() => window.location.href = 'mailto:support@agrisys.dk'}
                buttonType={ButtonTypes.textButton}
                overrides={{
                  baseButton: {...css.button_TextUnderline}
                }}
              />
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "center", marginTop: "2px" }}>support@agrisys.dk</p>
            </div>
            {message && <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, position: "absolute", top: mobile? "360px" : "370px" }}>{message}</p>}
          </div>
        </>
      </Modal>
    </frames.DecorBg>
  )
}
// ---------------------------------------------------
//#endregion

// #region | Create User Modal
function CreateUser({setNewState}:{setNewState:Dispatch<SetStateAction<Auth_States>>}) {
  const mobile = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileMax}px)`); 
  const {t} = useTranslation()
  const inputRefOne=useRef<InputFieldList_PropHandle>(null)
  const inputRefTwo=useRef<InputFieldList_PropHandle>(null)
  const [message, setMessage] = useState<string|null>(null)

  const { createNewUser } = useUser()

  const handleCreateUserBtn = () => {if (inputRefOne && inputRefOne.current && inputRefTwo && inputRefTwo.current) {
    const refsOne = inputRefOne.current.refs?.current;
    const refsTwo = inputRefTwo.current.refs?.current;

    if (refsOne && refsTwo) {
      var Username = inputRefOne.current.refs.current[0].context
      var Password = inputRefOne.current.refs.current[1].context
      var RepeatPassword = inputRefOne.current.refs.current[2].context
      var FirstName = inputRefTwo.current.refs.current[0].context
      var LastName = inputRefTwo.current.refs.current[1].context
      var Email = inputRefTwo.current.refs.current[2].context
      var Phone = inputRefTwo.current.refs.current[3].context

      if(!Username) {
        inputRefOne.current.refs.current[0].setError()
        inputRefOne.current.refs.current[0].focus()
        setMessage("User name is missing");
      }
      else if (!Password ) {
        inputRefOne.current.refs.current[1].setError()
        inputRefOne.current.refs.current[1].focus()
        setMessage("Password is missing");
      }
      else if (!RepeatPassword) {
        inputRefOne.current.refs.current[2].setError()
        inputRefOne.current.refs.current[2].focus()
        setMessage("Please repeat password");
      }
      else if (RepeatPassword !== Password) {
        inputRefOne.current.refs.current[2].setError()
        inputRefOne.current.refs.current[2].focus()
        setMessage("Password mismatch");
      }
      else {
        createNewUser(Username, Password, FirstName, LastName, Email, Phone)
        setMessage("Success");
        setTimeout(() => {
          setNewState(Auth_States.Login)
        }, 800);
      }
      setTimeout(() => {
        setMessage(null);
      }, 2000);
    }
  }}

  return (
    <frames.DecorBg>
      <Modal 
        modalHeader={t("loginFlow.createUser.header")}
        overrides={{ 
          isPrimaryComponent: true
        }}
        onGoBack={() => { setNewState(Auth_States.Login) }}
      >
        <div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px"}}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "start", justifyContent: "center", gap: "40px", width: mobile? "280px" : "fit-content"}}>
              <div style={{ display: "flex", flexDirection: "column"}}>
                <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 5px 22px" }}>{t("required")}</p>
                <InputFieldList ref={inputRefOne}
                  startEnhancers={[glyphs.User({size: "25px"}), glyphs.Password({size: "25px"}), glyphs.Password({size: "25px"})]}
                  placeholder={[t("user.userName")+"...", t("user.password")+"...", t("user.repeatPassword")+"..."]}
                  inputTypes={["text", "password", "password"]}
                  isRequired={[true, true, true]}
                  listStyle="vertical"
                  listGap="10px"
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column"}}>
                <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 5px 22px" }}>{t("optional")}</p>
                <InputFieldList ref={inputRefTwo}
                  startEnhancers={[glyphs.User({size: "25px"}), glyphs.User({size: "25px"}), glyphs.Mail({size: "25px"}), glyphs.Phone({size: "25px"})]}
                  placeholder={[t("user.firstName")+"...", t("user.lastName")+"...", t("user.email")+"...", t("user.phone")+"..."]}
                  inputTypes={["text", "text", "email", "tel"]}
                  isRequired={[true, true, true, true]}
                  listStyle="vertical"
                  listGap="10px"
                />
              </div>
            </div>
            {/* Create User Button */}
            <Button content={t("buttons.createBtn")} onClick={handleCreateUserBtn}
              buttonType={ButtonTypes.primaryButton}
              overrides={{
                baseButton: {...css.button_FitContent},
              }}
            />
            {message && <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, position: "absolute", top: mobile ? "520px" : "375px" }}>{message}</p>}
          </div>
        </div>
      </Modal>
    </frames.DecorBg>   
  )
}
// ---------------------------------------------------
//#endregion

// #region | Choose Site Modal
function SiteList({setNewState}:{setNewState:Dispatch<SetStateAction<Auth_States>>}) {
  const mobile = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileMax}px)`); 
  
  localStorage.setItem("activedomain", "")
  const { userFarms, getUserFarms, doLogOut } = useUser()
  var isTech = true

  const dispatch = useDispatch()
  const navigator = useNavigate()
  const {t} = useTranslation()

  useEffect(() => {
    getUserFarms()
  },[])

  const tableData = useMemo(() => (
    userFarms?.map((farm) => ({
      action: <Button content={farm.name}
        onClick={() => {
          dispatch(setStates({type:'farm', farm:farm.domain}));
          RootNavigation(navigator, Pages.dashboard)
        }} 
        buttonType={ButtonTypes.textButton}
        startEnhancer={<glyphs.ArrowSmooth fillColor={colors.greyDarkPrimary}/>}
        overrides={{
          contentStyles: {color: colors.greyDarkPrimary, ...fonts.fontSize4, ...fonts.paragraph},
          startEnhancerStyles: {marginRight: "15px"}
        }}
      />,
      id: farm.farm_id,
      farmName: farm.name,
      farmDomain: farm.domain,
    })) || []
  ), [dispatch, navigator, userFarms]);

  const accessorData = useMemo<GenericDataParameters[]>(() => ([
    {headerValue: "Site Name", accessor: "action", type: ListData_ValueTypes.Button},
    {headerValue: "Domain", accessor: "farmDomain", type: ListData_ValueTypes.String},
    {headerValue: "Id", accessor: "id", type: ListData_ValueTypes.Number},
  ]),[]);

  const columns = useEditableColumnConstructor({
    accessorData: [...accessorData], 
    tableStyle: TableStyles_Types.blueTable, 
    canEdit: false 
  });

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnPinning: {
        left: ["action"]
      }
    }
  })

  const reactiveTable = useMemo(() => {if (tableData.length!==0) {return ReactiveTable({table, tableType: TableStyles_Types.blueTable, emptyText: "Empty text", renderFooter: false})}}, [table, tableData.length])
  
  return (
    <frames.DecorBg>
      <Modal 
        modalHeader={t("loginFlow.chooseSite.header")}
        overrides={{ 
          isPrimaryComponent: false,
          root: { padding: "60px 5px 5px 5px" },
          background: colors.greyLightTertiary
        }}
        onLogoutBtn={() => { doLogOut() }}
      >
        <div style={{width: "100%", minWidth: "300px"}}>
        <div style={{position: "absolute", top: "10px", right: "0px", paddingRight: "10px"}}>
          <div style={{display: "flex", flexDirection: mobile? "column" : "row-reverse", justifyContent: "center", alignItems: "end", gap: "10px" }}>
              <Button content={"User Informations"}
                startEnhancer={<glyphs.User/>}
                onClick={() => {RootNavigation(navigator, Pages.userInformations)} } 
                buttonType={ButtonTypes.textButton}
              />
              {isTech && (
                <Button content={"Tools"}
                  startEnhancer={<glyphs.Setting/>}
                  buttonType={ButtonTypes.textButton}
                  onClick={() => {RootNavigation(navigator, Pages.graphQl)} } 
                />
              )}  
            </div>
          </div>
            {reactiveTable}
        </div>
      </Modal>
    </frames.DecorBg>
  )
}
// ---------------------------------------------------
//#endregion

// User Authentication
export default function Authentication_Path () {
  const [currentState, setCurState] = useState<Auth_States>(Auth_States.Login) 
  const { userToken } = useUser()

  console.log("hej", currentState)

  useEffect(() => {
    if (userToken) {
      setCurState(Auth_States.SiteList)
    }
    else setCurState(Auth_States.Login)
  }, [userToken])

  switch(currentState) {
    case Auth_States.Login:
      return <Login setNewState={setCurState}/>
    case Auth_States.ForgotPassword:
      return <ForgotPassword setNewState={setCurState}/>
    case Auth_States.ResetPassword:
      return <ResetPassword setNewState={setCurState}/>
    case Auth_States.CreateUser:
      return <CreateUser setNewState={setCurState}/>
    case Auth_States.SiteList:
      return <SiteList setNewState={setCurState}/>
    case undefined:
    default:
      return <></>
  }
}